import type { CSSProperties } from 'react'
import { cn } from '@/lib/utils'

/** 5×7 pixel question mark; colour comes from `currentColor`. */
const QUESTION_BITMAP = ['.###.', '#...#', '....#', '...#.', '..#..', '.....', '..#..']

export function PixelQuestion({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cn('grid grid-cols-5 gap-px', className)}>
      {QUESTION_BITMAP.flatMap((row, y) =>
        row
          .split('')
          .map((cell, x) => (
            <span key={`${x}-${y}`} className={cn('aspect-square rounded-[1px]', cell === '#' ? 'bg-current' : 'bg-transparent')} />
          )),
      )}
    </span>
  )
}

const BLOCKS = [
  { className: 'left-[3%] top-[12%] h-14 w-14', r: '-12deg', dur: '8s', lime: false },
  { className: 'right-[4%] top-[7%] h-10 w-10', r: '10deg', dur: '6.5s', lime: true },
  { className: 'bottom-[14%] right-[2.5%] hidden h-16 w-16 md:block', r: '-7deg', dur: '9s', lime: false },
  { className: 'bottom-[6%] left-[6%] hidden h-9 w-9 md:block', r: '14deg', dur: '7s', lime: true },
]

const SPARKS = [
  { left: '12%', top: '34%', size: 'h-2.5 w-2.5', dur: '3.6s', accent: false },
  { left: '28%', top: '9%', size: 'h-2 w-2', dur: '4.4s', accent: true },
  { left: '64%', top: '6%', size: 'h-3 w-3', dur: '5s', accent: false },
  { left: '86%', top: '40%', size: 'h-2 w-2', dur: '3.2s', accent: true },
  { left: '94%', top: '62%', size: 'h-2.5 w-2.5', dur: '4.8s', accent: false },
  { left: '4%', top: '72%', size: 'h-2 w-2', dur: '4s', accent: true },
]

/**
 * The FAQ's own backdrop: an isometric triangle lattice under two violet stage
 * beams, a lime horizon and floating glossy "?" blocks. CSS only.
 */
export function ArcadeBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="arc-environment absolute inset-0" />
      <div className="arc-lattice absolute inset-0" />
      <div className="arc-beam absolute -top-8 left-[10%] h-[72%] w-[22rem]" />
      <div className="arc-beam absolute -top-8 right-[8%] h-[82%] w-[26rem] [animation-delay:-6s]" />
      <div className="arc-horizon absolute inset-x-0 bottom-[22%] h-px" />

      {BLOCKS.map((block, index) => (
        <div
          key={index}
          className={cn('arc-bob absolute', block.className)}
          style={{ '--r': block.r, '--dur': block.dur } as CSSProperties}
        >
          <div className={cn('arc-block flex h-full w-full items-center justify-center', block.lime && 'arc-block-lime')}>
            <PixelQuestion className="w-[42%]" />
          </div>
        </div>
      ))}

      {SPARKS.map((spark, index) => (
        <span
          key={index}
          className={cn('arc-spark absolute', spark.size, spark.accent && 'arc-spark-accent')}
          style={{ left: spark.left, top: spark.top, '--dur': spark.dur } as CSSProperties}
        />
      ))}
    </div>
  )
}

/** A small glossy 3D headset with a lime mic tip and sound waves. */
export function HeadsetArt({ className }: { className?: string }) {
  return (
    <div aria-hidden className={cn('relative h-24 w-28 shrink-0', className)}>
      <div className="arc-floor absolute inset-x-4 -bottom-1 h-4 rounded-[50%]" />
      <div className="arc-bob absolute inset-0" style={{ '--dur': '5s' } as CSSProperties}>
        <div className="arc-band absolute left-[1.15rem] right-[1.15rem] top-1 h-16 rounded-t-full border-[6px] border-b-0" />
        <div className="arc-plastic absolute bottom-4 left-2 h-12 w-9 rounded-[40%]">
          <div className="arc-cushion absolute inset-y-1.5 right-1 w-2.5 rounded-full" />
        </div>
        <div className="arc-plastic absolute bottom-4 right-2 h-12 w-9 rounded-[40%]">
          <div className="arc-cushion absolute inset-y-1.5 left-1 w-2.5 rounded-full" />
          <span className="arc-blink absolute right-2 top-3 h-1.5 w-1.5 rounded-[1px] bg-primary" />
        </div>
        <div className="arc-mic absolute bottom-[1.35rem] left-7 h-1.5 w-11 origin-left rotate-[24deg] rounded-full">
          <span className="arc-mic-tip absolute -right-1 -top-[3px] h-3 w-3 rounded-full" />
        </div>
      </div>
      <span className="arc-wave absolute -right-3 top-6 h-10 w-6" />
      <span className="arc-wave absolute -right-6 top-4 h-14 w-8 [animation-delay:0.6s]" />
    </div>
  )
}
